/**
 * File: clients.ts
 * Path: packages/config/src/clients/clients.ts
 * Description: Clients: the businesses we have won work from. A won lead
 * becomes a client under the same document id, so a lead can only ever turn
 * into one client and its history stays one click away. Clients from before
 * the pipeline existed are added by hand under a generated id.
 *
 * Case studies hang off the client (`projectIds`), not the lead: a client can
 * have several projects, and not every client becomes a case study.
 */

import {
  readFlag,
  readMillis,
  readNumber,
  readText,
  safeWebUrl,
  type Lead,
} from "../leads/leads";
import { resolveConsent, type Consent } from "../consent/consent";
import { formatClientNumber } from "../references/references";
import {
  toRecordBranches,
  toRecordContacts,
  type RecordBranch,
  type RecordContact,
} from "../leads/people";

/** Same reasoning as LEADS_PIPELINE_LIMIT: one cheap read, nobody hidden. */
export const CLIENTS_LIST_LIMIT = 500;

/** What kind of record this is. Won work is a "client"; the others are set by hand. */
export const CLIENT_TYPES = ["client", "lead", "partner", "past"] as const;
export type ClientType = (typeof CLIENT_TYPES)[number];

export const CLIENT_TYPE_LABELS: Record<ClientType, string> = {
  client: "Client",
  lead: "Lead",
  partner: "Partner",
  past: "Past client",
};

export const toClientType = (value: unknown): ClientType =>
  CLIENT_TYPES.find((t) => t === value) ?? "client";

export interface Client {
  id: string;
  /** Client, Lead, Partner (refers work to us) or Past client; "client" when never set. */
  type: ClientType;
  /**
   * C001, C002... in the order clients were won (references/references.ts).
   * Assigned once from counters/clients and never reused; null for a client
   * from before numbering until "Number existing clients" is run.
   */
  clientNumber: number | null;
  /** How many contracts have been numbered for this client (02 in IDK-C012-02). */
  contractCount: number;
  /** Business name. */
  name: string;
  contactName: string;
  phone: string;
  email: string;
  /** http(s) only; anything else is dropped when read. */
  website: string;
  address: string;
  /** Company registration or ID number, printed on their invoices and contracts. */
  registrationNo: string;
  /** What the business does, e.g. "Plumber". */
  niche: string;
  /** "How did you hear about us?" in their own words: a cross-check on the recorded source. */
  heardAbout: string;
  /** Anything worth remembering: what they bought, how they like to work. */
  notes: string;
  /** Agreed job value in rand, when known. */
  value: number | null;
  /** YYYY-MM-DD the work was won. */
  since: string | null;
  /** POPIA: they asked not to be contacted. */
  doNotContact: boolean;
  /** POPIA: they agreed to hear from us by email, SMS or WhatsApp. */
  marketingConsent: boolean;
  /** POPIA consent (consent/consent.ts): existing client unless they said yes or stop. */
  consent: Consent;
  /** The lead this client was won from; null for clients added by hand. */
  leadId: string | null;
  /** Portfolio case studies made for this client, oldest first. */
  projectIds: string[];
  /** Everyone else to speak to, beyond the main contact (leads/people.ts). */
  contacts: RecordContact[];
  branches: RecordBranch[];
  createdAt: number | null;
  updatedAt: number | null;
}

/** The fields a person edits on a client. */
export type ClientDetails = Pick<
  Client,
  | "name"
  | "contactName"
  | "phone"
  | "email"
  | "website"
  | "address"
  | "registrationNo"
  | "niche"
  | "heardAbout"
  | "notes"
  | "value"
  | "since"
  | "doNotContact"
  | "marketingConsent"
>;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Reads a stored client, filling gaps so the admin never renders undefined. */
export const toClient = (id: string, data: Record<string, unknown>): Client => {
  const value = readNumber(data.value);
  const since = readText(data.since);
  const projectIds = Array.isArray(data.projectIds)
    ? data.projectIds.filter(
        (p): p is string => typeof p === "string" && p.length > 0,
      )
    : [];
  const clientNumber = readNumber(data.clientNumber);
  const contractCount = readNumber(data.contractCount);
  return {
    id,
    type: toClientType(data.type),
    clientNumber:
      clientNumber !== null &&
      Number.isSafeInteger(clientNumber) &&
      clientNumber >= 1
        ? clientNumber
        : null,
    contractCount:
      contractCount !== null &&
      Number.isSafeInteger(contractCount) &&
      contractCount >= 0
        ? contractCount
        : 0,
    name: readText(data.name) || "Unnamed business",
    contactName: readText(data.contactName),
    phone: readText(data.phone),
    email: readText(data.email),
    website: safeWebUrl(data.website),
    address: readText(data.address),
    registrationNo: readText(data.registrationNo).slice(0, 60),
    niche: readText(data.niche),
    heardAbout: readText(data.heardAbout).slice(0, 200),
    notes: readText(data.notes),
    value: value !== null && value >= 0 ? value : null,
    since: ISO_DATE.test(since) ? since : null,
    doNotContact: readFlag(data.doNotContact),
    marketingConsent: readFlag(data.marketingConsent),
    consent: resolveConsent({
      stored: data.consent,
      doNotContact: readFlag(data.doNotContact),
      marketingConsent: readFlag(data.marketingConsent),
      firstMessageSent: false,
      isClient: true,
    }),
    leadId: readText(data.leadId) || null,
    projectIds: [...new Set(projectIds)],
    contacts: toRecordContacts(data.contacts),
    branches: toRecordBranches(data.branches),
    createdAt: readMillis(data.createdAt),
    updatedAt: readMillis(data.updatedAt),
  };
};

// ─── Client numbers ──────────────────────────────────────────────────────

/** counters/clients: { last, lastClientId }. Created by the first numbering. */
export const CLIENT_COUNTER = {
  collection: "counters",
  doc: "clients",
} as const;

/** The client's number as printed ("C012"), or null before it has one. */
export const clientNumberLabel = (
  client: Pick<Client, "clientNumber">,
): string | null =>
  client.clientNumber === null ? null : formatClientNumber(client.clientNumber);

/**
 * The clients "Number existing clients" numbers, in the order they get
 * their numbers: oldest first by when they were added (createdAt, or the
 * date the work was won when that is missing), then by id so the order is
 * stable. Clients that already have a number are left out: a number, once
 * given, never changes.
 */
export const clientsToNumber = <
  T extends Pick<Client, "id" | "clientNumber" | "createdAt" | "since">,
>(
  clients: readonly T[],
): T[] => {
  const sinceMs = (since: string | null): number =>
    since ? Date.parse(`${since}T00:00:00Z`) : Number.POSITIVE_INFINITY;
  const when = (c: T): number => c.createdAt ?? sinceMs(c.since);
  return clients
    .filter((c) => c.clientNumber === null)
    .sort((a, b) => {
      // Compared, not subtracted: two undated clients are Infinity apart.
      if (when(a) !== when(b)) return when(a) < when(b) ? -1 : 1;
      return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
    });
};

/**
 * The stored shape of a client won from a lead: its contact details, POPIA
 * flags and value carry over, so nothing is typed twice.
 */
export const clientFromLead = (lead: Lead, since: string) => ({
  name: lead.name,
  contactName: lead.contactName,
  phone: lead.phone,
  email: lead.email,
  website: lead.website,
  address: lead.address,
  niche: lead.niche,
  notes: "",
  value: lead.value,
  since,
  doNotContact: lead.doNotContact,
  marketingConsent: lead.marketingConsent,
  leadId: lead.id,
  projectIds: [] as string[],
  contacts: lead.contacts,
  branches: lead.branches,
});

/**
 * Client history (clients/<id>/activity): notes and logged contact. Same
 * shape as a lead's history, append-only and signed by its author (byUid),
 * so a won client's timeline reads the same as the lead's before it.
 */
export const CLIENT_ACTIVITY_TYPES = [
  "note",
  "call",
  "whatsapp",
  "email",
  "meeting",
] as const;

export type ClientActivityType = (typeof CLIENT_ACTIVITY_TYPES)[number];

export const CLIENT_ACTIVITY_LABELS: Record<ClientActivityType, string> = {
  note: "Note",
  call: "Call",
  whatsapp: "WhatsApp",
  email: "Email",
  meeting: "Meeting",
};

/** Longest note or log entry, matched by firestore.rules. */
export const CLIENT_ACTIVITY_MAX_LENGTH = 2000;

export interface ClientActivity {
  id: string;
  type: ClientActivityType;
  text: string;
  /** Email of whoever wrote it. */
  byEmail: string;
  at: number | null;
}

export const isClientActivityType = (
  value: unknown,
): value is ClientActivityType =>
  CLIENT_ACTIVITY_TYPES.some((type) => type === value);

export const toClientActivity = (
  id: string,
  data: Record<string, unknown>,
): ClientActivity => ({
  id,
  type: isClientActivityType(data.type) ? data.type : "note",
  text: readText(data.text),
  byEmail: readText(data.byEmail),
  at: readMillis(data.createdAt),
});
